import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const api = axios.create({ baseURL: API_BASE, timeout: 15000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r.data,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_info');
      window.location.href = '/login';
    }
    return Promise.reject(new Error(err.response?.data?.message || 'Request failed'));
  }
);

export const adminAPI = {
  // Whether the BC and Shopify syncs are actually working, and whether any
  // member is stranded as a result.
  getSyncHealth:  () => api.get('/membership/sync-health'),

  // Receipts a member has said are not theirs. Both a billing complaint and a
  // fraud signal, so they need a queue rather than a support inbox.
  getDisputes:      (status = 'open') => api.get('/purchases/disputes', { params: { status } }),
  getDisputeCount:  ()                 => api.get('/purchases/disputes/count'),
  updateDispute:  (id, body)        => api.put(`/purchases/disputes/${id}`, body),

  // Receipts a member has said are not theirs. Both a billing complaint and a
  // fraud signal, so they surface here rather than in a support inbox.
  getDisputes:    (status = 'open') => api.get('/purchases/disputes', { params: { status } }),
  updateDispute:  (id, body) => api.put(`/purchases/disputes/${id}`, body),

  // Business Central's own member directory, mirrored into bc_members so the
  // admin can page through 23k customers without calling BC per request.
  getBCMembers:     (params) => api.get('/admin/bc-members', { params }),
  // Who a BC segment actually reaches, and which records hold a broken number.
  auditAudience:    (audience_config) => api.post('/admin/campaigns/audience/audit', { audience_config }),
  exportAudience:   (audience_config) => api.post('/admin/campaigns/audience/export', { audience_config }, { responseType: 'blob' }),
  getBCMemberStats: () => api.get('/admin/bc-members/stats'),
  verifyQR:         (value) => api.post('/admin/qr/verify', { value }),
  // Mirroring 23k members takes ~45s. The shared 15s timeout would abort the
  // request while the sync ran on regardless, so the button always reported a
  // failure that had not happened.
  syncBCMembers:    () => api.post('/admin/bc-members/sync', null, { timeout: 180000 }),
  // Auth
  login: (email, password) => api.post('/admin/login', { email, password }),
  changePassword: (currentPassword, newPassword) =>
    api.post('/admin/change-password', { currentPassword, newPassword }),

  // Dashboard
  getDashboard:         () => api.get('/admin/dashboard'),
  getDashboardGrowth:   () => api.get('/admin/dashboard/growth'),
  getDashboardActivity: () => api.get('/admin/dashboard/activity'),
  getDashboardExpiring: () => api.get('/admin/dashboard/expiring'),

  // Members
  getMembers:        (params) => api.get('/admin/members', { params }),
  getMember:         (id) => api.get(`/admin/members/${id}`),
  deactivateMember:  (id) => api.post(`/admin/members/${id}/deactivate`),
  deleteMember:      (id) => api.delete(`/admin/members/${id}`),
  updateMemberStatus:(id, status) => api.post(`/admin/members/${id}/status`, { status }),
  exportMembers:     () => api.get('/admin/members/export', { responseType: 'blob' }),

  // Coupons
  getCoupons:   (params) => api.get('/admin/coupons', { params }),

  // Referrals
  getReferrals: () => api.get('/admin/referrals'),

  // Business Central membership reconciliation. dryRun reports what would
  // change without writing — worth running first, since this can downgrade.
  // Same reason as syncBCMembers above. The dry run answers in a second
  // because it only reads, so the preview looked fine and applying always
  // said "Request failed" — while the sync carried on and finished.
  bcSync: (dryRun = false) =>
    api.post('/admin/bc-sync', { dryRun }, { timeout: dryRun ? 60000 : 180000 }),

  // Loyalty
  getLoyaltySettings:   () => api.get('/admin/loyalty/settings'),
  saveLoyaltySettings:  (data) => api.put('/admin/loyalty/settings', data),
  getLoyaltyPartners:   () => api.get('/admin/loyalty/partners'),
  createPartner:        (data) => api.post('/admin/loyalty/partners', data),
  updatePartner:        (id, data) => api.put(`/admin/loyalty/partners/${id}`, data),
  deletePartner:        (id) => api.delete(`/admin/loyalty/partners/${id}`),
  getLoyaltyLeaderboard:() => api.get('/admin/loyalty/leaderboard'),

  // App pages
  getPages:  () => api.get('/admin/pages'),
  getPage:   (slug) => api.get(`/admin/pages/${slug}`),
  savePage:  (slug, data) => api.put(`/admin/pages/${slug}`, data),

  // Settings
  getSettings:  () => api.get('/admin/settings'),
  saveSettings: (settings) => api.put('/admin/settings', { settings }),

  // CRM
  getCRMSegments:       () => api.get('/admin/crm/segments'),
  getCRMSegment:        (name, page = 1) => api.get(`/admin/crm/segment/${name}?page=${page}`),
  exportCRMSegment:     (name) => api.get(`/admin/crm/segment/${name}/export`, { responseType: 'blob' }),

  // Push Notifications
  getPushCampaigns: () => api.get('/admin/push/campaigns'),
  sendPushNotification: (data) => api.post('/admin/push/send', data),
  // Same payload, your own device only. A push cannot be recalled.
  testPushNotification: (data) => api.post('/admin/push/test', data),

  // WhatsApp Campaigns
  getCampaigns:             () => api.get('/admin/campaigns'),
  createCampaign:           (data) => api.post('/admin/campaigns', data),
  getCampaign:              (id) => api.get(`/admin/campaigns/${id}`),
  sendCampaign:             (id) => api.post(`/admin/campaigns/${id}/send`),
  cancelCampaign:           (id) => api.post(`/admin/campaigns/${id}/cancel`),
  deleteCampaign:           (id) => api.delete(`/admin/campaigns/${id}`),
  getCampaignRecipients:    (id, page = 1) => api.get(`/admin/campaigns/${id}/recipients?page=${page}`),
  exportCampaignRecipients: (id) => api.get(`/admin/campaigns/${id}/export`, { responseType: 'blob' }),
  previewAudience:          (data) => api.post('/admin/campaigns/audience/preview', data),
  uploadAudience:           (formData) => api.post('/admin/campaigns/audience/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getWaTemplates:           () => api.get('/admin/campaigns/templates'),
  createWaTemplate:         (data) => api.post('/admin/campaigns/templates', data),
  deleteWaTemplate:         (id) => api.delete(`/admin/campaigns/templates/${id}`),
  syncWaTemplates:          () => api.post('/admin/campaigns/templates/sync'),

  // Promo Codes
  getPromoCodes:           () => api.get('/admin/promo-codes'),
  createPromoCode:         (data) => api.post('/admin/promo-codes', data),
  updatePromoCode:         (id, data) => api.put(`/admin/promo-codes/${id}`, data),
  deletePromoCode:         (id) => api.delete(`/admin/promo-codes/${id}`),
  getPromoRedemptions:     (id, page = 1) => api.get(`/admin/promo-codes/${id}/redemptions?page=${page}`),
  exportPromoRedemptions:  (id) => api.get(`/admin/promo-codes/${id}/export`, { responseType: 'blob' }),

  // Activity audit log
  getActivations:    (params) => api.get('/admin/activations', { params }),
  getAbandoned:      (params) => api.get('/admin/abandoned', { params }),
  // The till. A store login reaches these and nothing else.
  storeLookup:       (mobile) => api.get('/store/lookup', { params: { mobile } }),
  storeActivate:     (body)   => api.post('/store/activate', body),
  storePayments:     (params) => api.get('/store/payments', { params }),
  // staffId names who is at the till. The page already knows it -- every
  // other counter action sends it -- and without it a scan is recorded
  // against nobody, which is the one thing an audit trail cannot be.
  storeQR:           (value, staffId) => api.post('/store/qr', { value, staffId }),

  // The shop networks a membership can legitimately be shown from.
  storeNetworks:       ()        => api.get('/admin/store-networks'),
  addStoreNetwork:     (body)    => api.post('/admin/store-networks', body),
  deleteStoreNetwork:  (id)      => api.delete(`/admin/store-networks/${id}`),
  myIp:                ()        => api.get('/admin/my-ip'),
  scanAlerts:          (params)  => api.get('/admin/scan-alerts', { params }),
  scanAlertCount:      ()        => api.get('/admin/scan-alerts/count'),
  pushAudiences:       ()        => api.get('/admin/push/audiences'),

  // Dashboard users. Owner only -- the server refuses these for anybody else.
  me:                       ()          => api.get('/admin/me'),
  dashboardUsers:           ()          => api.get('/admin/users'),
  createDashboardUser:      (body)      => api.post('/admin/users', body),
  updateDashboardUser:      (id, body)  => api.put(`/admin/users/${id}`, body),
  setDashboardUserPassword: (id, password) => api.post(`/admin/users/${id}/password`, { password }),
  usersAudit:               ()          => api.get('/admin/users-audit'),
  ackScanAlert:        (id, body) => api.post(`/admin/scan-alerts/${id}/ack`, body),
  storeSummary:      ()       => api.get('/store/summary'),
  storeStaff:        ()       => api.get('/store/staff'),
  storeRenew:        (body)   => api.post('/store/renew', body),
  storeReverse:      (body)   => api.post('/store/reverse', body),
  storeRecent:       (params) => api.get('/store/recent', { params }),
  // Audit and KPIs. `params` takes either {days} or {from,to}; format:'csv'
  // returns a blob, so the caller must ask for one.
  storeActivations:  (params) => api.get('/store/activations', { params }),
  storeMemberships:  (params) => api.get('/store/memberships', { params }),
  storeKpis:         (params) => api.get('/store/kpis', { params }),
  storeExport:       (what, params) =>
    api.get(`/store/${what}`, { params: { ...params, format: 'csv' }, responseType: 'blob' }),

  // Counter staff, managed by a manager rather than typed at the terminal.
  getStoreStaff:     ()           => api.get('/admin/store-staff'),
  addStoreStaff:     (data)       => api.post('/admin/store-staff', data),
  updateStoreStaff:  (id, data)   => api.put(`/admin/store-staff/${id}`, data),
  deleteStoreStaff:  (id)         => api.delete(`/admin/store-staff/${id}`),
  exportActivations: (params) => api.get('/admin/activations/export', { params, responseType: 'blob' }),

  // Refunds. Three systems have to agree -- the gateway, Business Central and
  // us -- so the preview is read-only and the POST reports each step.
  refundablePayments: (params) => api.get('/refunds/refundable', { params }),
  refundPreview:      (id)     => api.get(`/refunds/payment/${id}`),
  refundPayment:      (id, data) => api.post(`/refunds/payment/${id}`, data),

  // Family Members (admin)
  getMemberFamily:         (userId) => api.get(`/admin/members/${userId}/family`),
  addMemberFamily:         (userId, data) => api.post(`/admin/members/${userId}/family`, data),
  revokeMemberFamily:      (userId, familyId) => api.delete(`/admin/members/${userId}/family/${familyId}`),
  reactivateMemberFamily:  (userId, familyId) => api.post(`/admin/members/${userId}/family/${familyId}/reactivate`),
};

export default api;
